import { EOnboaringFormStep, ERegion, getOnboardingStepsByKey, EOnboardingKey } from '@constants';
import { Form } from 'antd';
import { CarouselRef } from 'antd/es/carousel';
import { useMemo } from 'react';
import { create } from 'zustand';

import { useOnboardingForm } from '@/providers';
import { useMutatePlanification, useMutateMySubscriptions, useProfile, useAcademy } from '@/store';
import { getUserSubverticals } from '@/utils/getUserSubverticals';

type IStore = {
  stepIndex: number;
  isOnboardingStarted: boolean;
  carouselRef: React.RefObject<CarouselRef>;
  examGroupDate: Date | undefined;
  loadingAfterContract: boolean;
  setExamGroupDate: (examGroupDate: Date | undefined) => void;
  setStepIndex: (stepIndex: number) => void;
  startOnboarding: () => void;
  startLoadingAfterContract: () => void;
};

const store = create<IStore>(set => ({
  stepIndex: 0,
  isOnboardingStarted: false,
  carouselRef: { current: null },
  examGroupDate: undefined,
  loadingAfterContract: false,
  setExamGroupDate: examGroupDate => set({ examGroupDate }),
  setStepIndex: stepIndex => set({ stepIndex }),
  startOnboarding: () => {
    set({ isOnboardingStarted: true });
  },
  startLoadingAfterContract: () => {
    set({ loadingAfterContract: true });
  }
}));

export const useOnboardingFormLogic = () => {
  const {
    stepIndex,
    setStepIndex,
    isOnboardingStarted,
    startOnboarding,
    carouselRef,
    examGroupDate,
    setExamGroupDate,
    startLoadingAfterContract,
    loadingAfterContract
  } = store();
  const { setMyVisibleSubscriptions } = useMutateMySubscriptions();
  const { profile } = useProfile();
  const academyId = profile?.academy?._id;
  const { academy } = useAcademy(academyId);
  const { createPlanification, isWorking: isPlanificationLoading } = useMutatePlanification();
  const form = useOnboardingForm();
  const planification = Form.useWatch(['planification'], form);

  const shouldCreatePlanificationOnThisStep = () => {
    const key = academy?.onboarding?.onboardingKey as EOnboardingKey | undefined;
    if (key === EOnboardingKey.FP) return currentStep === EOnboaringFormStep.STUDY_PLAN_FP;
    if (key === EOnboardingKey.UNI) return currentStep === EOnboaringFormStep.CHOOSE_COURSES;
    if (key === EOnboardingKey.OPOS) return currentStep === EOnboaringFormStep.STUDY_PLAN;
    return false;
  };

  const handleNextStep = () => {
    if (!carouselRef.current || !profile) return;
    if (shouldCreatePlanificationOnThisStep()) {
      createPlanification(
        {
          ...planification,
          userId: profile._id,
          includeAllActivities: true,
          endDate: planification?.endDate ? new Date(planification?.endDate) : undefined,
          restDates: (planification?.restDates ?? []).map((date: Date) => new Date(date)),
          restWeekdays: planification?.restWeekdays ?? []
        },
        {
          onSuccess: async () => {
            const selected = planification?.subscriptions;
            if (Array.isArray(selected) && selected.length > 0) {
              await setMyVisibleSubscriptions(selected);
            }
            setStepIndex(stepIndex + 1);
            carouselRef.current?.next();
          }
        }
      );
      return;
    }
    if (currentStep === EOnboaringFormStep.CHOSEN_REASONS) {
      form.submit();
    }
    setStepIndex(stepIndex + 1);
    carouselRef.current.next();
  };

  const handlePreviousStep = () => {
    if (!carouselRef.current) return;
    setStepIndex(stepIndex - 1);
    carouselRef.current.prev();
  };

  const selectedSteps = useMemo(() => {
    const region = profile?.regionConfig ?? ERegion.ES;
    const key = academy?.onboarding?.onboardingKey;
    const userSubverticals = getUserSubverticals(profile);

    return getOnboardingStepsByKey(key, region, userSubverticals);
  }, [profile?.regionConfig, profile?.tags, academy?.onboarding?.onboardingKey]);

  const currentStep = selectedSteps[stepIndex];
  const stepLength = selectedSteps.length;

  return {
    form,
    carouselRef,
    currentStep,
    handleNextStep,
    isOnboardingStarted,
    selectedSteps,
    startOnboarding,
    setStepIndex,
    handlePreviousStep,
    stepLength,
    stepIndex,
    examGroupDate,
    setExamGroupDate,
    startLoadingAfterContract,
    loadingAfterContract,
    isNextStepLoading: isPlanificationLoading,
    isOnboardingFinished: currentStep === EOnboaringFormStep.FINAL_STEP
  };
};
